import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { RootState } from '../store';
import { useGetUsersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { WebFormSubmission } from '../api/generated/model';
import FormSubmissionsTable, { formSubmissionFilterKeys, ALL_STATUS_VALUES } from '../components/FormSubmissionsTable';
import FormSubmissionDetailModal from '../components/FormSubmissionDetailModal';

const FormSubmissionsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial state hydrated from the URL once, so a filtered view is
  // linkable and survives a reload.
  const [initialPeriod] = useState<string>(() => searchParams.get('period') || 'month');
  const [initialDateRange] = useState<{ start_date: string; end_date: string }>(() => {
    const urlStart = searchParams.get('start_date');
    const urlEnd = searchParams.get('end_date');
    if (urlStart && urlEnd) return { start_date: urlStart, end_date: urlEnd };
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
    };
  });
  const [initialFilters] = useState<Record<string, any>>(() => {
    const f: Record<string, any> = {};
    for (const key of formSubmissionFilterKeys) {
      const v = searchParams.get(key);
      // The deselect-all sentinel is an empty string, not null. Testing
      // truthiness here dropped it, so `?status=` came back as "no filter at
      // all" and the view silently widened to every row on reload — the
      // opposite of what the user selected. The write side below is careful
      // about this for the same reason.
      if (v !== null) f[key] = v;
    }
    // Default to accepted-only so bot spam and quarantined rows stay out of
    // the way. Applied HERE rather than as a server-side default so the
    // dropdown actually shows it: a default the filter control cannot
    // represent would leave every box ticked over a filtered query.
    // searchParams.has() distinguishes "no filter chosen yet" from the
    // deselect-all sentinel (`?status=`), which must stay empty.
    if (!searchParams.has('status')) {
      f.status = 'accepted';
    }
    return f;
  });
  const [initialSort] = useState<{ field: string; direction: 'asc' | 'desc' }>(() => ({
    field: searchParams.get('sort_field') || 'created_at',
    direction: (searchParams.get('sort_direction') as 'asc' | 'desc') || 'desc',
  }));

  const [period, setPeriod] = useState(initialPeriod);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState(initialSort);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    if (period !== 'all') {
      if (dateRange.start_date) params.set('start_date', dateRange.start_date);
      if (dateRange.end_date) params.set('end_date', dateRange.end_date);
    }
    for (const key of formSubmissionFilterKeys) {
      // `?status=` is the deselect-all sentinel and must survive into the
      // URL, so this tests for undefined rather than truthiness. Dropping it
      // would make a reload silently widen the filter instead of restoring
      // what the user selected.
      if (filters[key] !== undefined) {
        params.set(key, filters[key]);
      } else if (key === 'status') {
        // The filter control collapses "every option checked" to no
        // parameter at all, which on reload is indistinguishable from "never
        // chose anything" — so the accepted-only default would reapply and
        // silently undo the user's choice. Writing the full set explicitly
        // makes the two states distinguishable. Only `status` needs this,
        // because it is the only filter with a non-neutral default.
        params.set(key, ALL_STATUS_VALUES.join(','));
      }
    }
    if (sort.field !== 'created_at') params.set('sort_field', sort.field);
    if (sort.direction !== 'desc') params.set('sort_direction', sort.direction);
    setSearchParams(params, { replace: true });
  }, [period, dateRange, filters, sort, setSearchParams]);

  const [selected, setSelected] = useState<WebFormSubmission | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['web-form-submissions:form_submissions'] });
    setRefreshKey(k => k + 1);
  }, [queryClient]);

  // useCallback so the table's notification effects don't see a fresh
  // reference each render — that would re-fire the effect, push state up,
  // re-render, mint a new ref, ad infinitum.
  const handleDateRangeChange = useCallback(
    (r: { start_date: string; end_date: string }, p: string) => {
      setDateRange(r);
      setPeriod(p);
    },
    []
  );
  const handleFiltersChange = useCallback((f: Record<string, any>) => setFilters(f), []);
  const handleSortChange = useCallback(
    (field: string, direction: 'asc' | 'desc') => setSort({ field, direction }),
    []
  );
  const handleRowClick = useCallback((s: WebFormSubmission) => setSelected(s), []);

  return (
    <DashboardLayout title="Form Submissions" subtitle="Contact form submissions from arrayscape.com" scrollable>
      <FormSubmissionsTable
        initialPeriod={initialPeriod}
        initialDateRange={initialDateRange}
        initialFilters={initialFilters}
        initialSort={initialSort}
        onDateRangeChange={handleDateRangeChange}
        onFiltersChange={handleFiltersChange}
        onSortChange={handleSortChange}
        onRowClick={handleRowClick}
        preferencesKey="form_submissions"
        userId={user?.id}
        userPreferences={userData?.preferences}
        refreshKey={refreshKey}
      />

      {selected && (
        <FormSubmissionDetailModal
          key={selected.id}
          submission={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      )}
    </DashboardLayout>
  );
};

export default FormSubmissionsPage;
